import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCredentials,
  saveCredentials,
  clearMessage,
} from "../reducers/credentialsSlice";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

const CredentialsFrame = () => {
  const dispatch = useDispatch();

  const { creds, status, error, message, deploymentType } = useSelector(
    (state) => state.credentials,
  );

  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );

  const has_write = useSelector(
    (state) => state.authen.loggedInUser?.license_role === "Change",
  );

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [organization, setOrganization] = useState("");

  // Controls whether Organization ID is locked in the UI.
  const [organizationSaved, setOrganizationSaved] = useState(false);

  // Confirmation dialog state
  const [openOrgConfirm, setOpenOrgConfirm] = useState(false);

  useEffect(() => {
    if (license_key) {
      dispatch(fetchCredentials({ license_key }));
    }
  }, [dispatch, license_key]);

  useEffect(() => {
    if (creds) {
      setUsername(creds.username || "");
      setPassword(creds.password || "");
      setOrganization(creds.organization || "");

      // Lock Organization ID if it already exists in the backend.
      setOrganizationSaved(!!creds.organization);
    }
  }, [creds]);

  const handleSave = () => {
    if (!username || !password || !organization) {
      return alert("All fields are required.");
    }

    // Organization ID is being added for the first time.
    if (!organizationSaved) {
      setOpenOrgConfirm(true);
      return;
    }

    // Organization ID already exists.
    // Save username/password changes directly.
    dispatch(
      saveCredentials({
        license_key,
        username,
        password,
        organization,
      }),
    );
  };

  const handleConfirmOrgSave = () => {
    // Close confirmation popup.
    setOpenOrgConfirm(false);

    // Immediately lock the Organization ID field.
    // This does not wait for Redux or a page refresh.
    setOrganizationSaved(true);

    dispatch(
      saveCredentials({
        license_key,
        username,
        password,
        organization,
      }),
    );
  };

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          width: 400,
          pointerEvents: has_write ? "auto" : "none",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            color: "#625D5D",
            mb: 0.5,
          }}
        >
          {creds ? "Update EMIS Credentials" : "Add EMIS Credentials"}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "error.main",
            mb: 2,
          }}
        >
          Organization Id once added cannot be updated
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {message && (
          <Alert
            severity="success"
            onClose={() => dispatch(clearMessage())}
            sx={{ mb: 2 }}
          >
            {message}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          margin="normal"
          disabled={deploymentType === "runner"}
        />

        <TextField
          fullWidth
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
          disabled={deploymentType === "runner"}
        />

        <TextField
          fullWidth
          label="Organization ID"
          value={organization}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, "");
            setOrganization(value);
          }}
          margin="normal"
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
          }}
          disabled={deploymentType === "runner" || organizationSaved}
        />

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={
              deploymentType === "runner" || status === "saving" || !has_write
            }
          >
            {status === "saving" ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : creds ? (
              "Update"
            ) : (
              "Save"
            )}
          </Button>
        </Box>
      </Paper>

      <Dialog
        open={openOrgConfirm}
        onClose={() => setOpenOrgConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Organization ID</DialogTitle>

        <DialogContent>
          <Typography>Organization Id once added cannot be updated</Typography>

          <Typography
            variant="body2"
            sx={{
              mt: 1.5,
              color: "text.secondary",
            }}
          >
            Are you sure you want to save this Organization ID?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenOrgConfirm(false)}>Cancel</Button>

          <Button
            onClick={handleConfirmOrgSave}
            variant="contained"
            color="primary"
          >
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CredentialsFrame;
